import { filter } from '@audience-republic/audience-manager-shared/utils/api/filterBuilder'
import type { Condition, Param } from '@audience-republic/audience-manager-shared/utils/api/filterBuilder'
import type { FetchMessageRecipientsParams, MessageRecipient } from './types'
import type { EndpointKey } from '@/api/api.config'
import { getEffectiveFilterEngine } from '@/utils/filterEngine'
import { makeSafeOrderbyParams } from '@/utils/api/makeSafeOrderbyParams'

function toStatusClause(item: string): string {
  return item.startsWith('not-')
    ? `status != ${item.replace(/^not-/i, '')}`
    : `status=${item}`
}

/**
 * Builds the `status` part of a fan-message `$filter`. Statuses prefixed with
 * `not-` are negated (e.g. `not-opened` -> `status != opened`).
 */
export function generateStatusFilter(
  statuses: string[],
  combineStatusOp: 'AND' | 'OR' = 'OR',
): string | undefined {
  if (statuses.length > 1) {
    return `(${statuses.map(toStatusClause).join(` ${combineStatusOp} `)})`
  }
  if (statuses.length === 1 && statuses[0] != null) {
    return toStatusClause(statuses[0])
  }
  return undefined
}

/**
 * Escapes a user-typed search for the `$filter` DSL: the value is parsed as a quoted
 * string literal by the server (`\` and `"` must be escaped) and then used in a LIKE
 * pattern where `%` and `_` are wildcards (escaped with a backslash, which itself
 * has to be escaped in the string literal).
 */
export function escapeSearchString(searchString: string): string {
  return searchString
    .replace(/\\/g, '\\\\\\\\')
    .replace(/%/g, '\\\\%')
    .replace(/_/g, '\\\\_')
    .replace(/"/g, '\\"')
}

function buildSearchCondition(searchString: string, searchStringTargets: string[]): Condition {
  const value = `%${escapeSearchString(searchString)}%`
  return ['or', ...searchStringTargets.map((target): Param => ({ field: target, op: 'ilike', value }))]
}

/** `$filter` part matching a search string on any of the targets, undefined if there is nothing to search. */
export function generateSearchFilter(
  searchString: string,
  searchStringTargets: string[],
): string | undefined {
  if (!searchString || searchStringTargets.length === 0) return undefined
  return filter(buildSearchCondition(searchString, searchStringTargets))
}

export interface MessageRecipientsRequest {
  endpointKey: EndpointKey
  params: Record<string, unknown>
}

/**
 * Picks the endpoint (regular, sms, suppressed `fan-message-excluded` or clicked-url
 * `filter-fan-message`, like AM2 does) and builds its query params.
 */
export function buildMessageRecipientsRequest({
  messageTaskOid,
  top,
  skip,
  select = null,
  orderBy,
  searchString = null,
  searchStringTargets,
  statuses,
  combineStatusOp,
  isSms,
  excludeRejected,
  clickedUrlFilter = null,
}: FetchMessageRecipientsParams): MessageRecipientsRequest {
  const baseParams: Record<string, unknown> = {
    $top: top,
    $skip: skip,
    ...(select && { $select: select.join(',') }),
  }

  if (statuses[0] === 'suppressed') {
    const searchCondition = searchString && searchStringTargets.length > 0
      ? buildSearchCondition(searchString, searchStringTargets)
      : null
    return {
      endpointKey: 'fetchSuppressedFanMessages',
      params: {
        ...baseParams,
        $orderby: makeSafeOrderbyParams(orderBy),
        $filter: filter(
          { field: 'taskOid', op: '=', value: Number(messageTaskOid) },
          { field: 'status', op: '=', value: 'suppressed' },
          searchCondition,
        ),
      },
    }
  }

  if (clickedUrlFilter) {
    return {
      endpointKey: 'fetchClickedUrlFanMessages',
      params: {
        ...baseParams,
        $taskOid: messageTaskOid,
        // clickedUrlFilter is an array like ["clicked", "https://www.audiencerepublic.com/"]
        $clickedUrl: clickedUrlFilter[1],
        ...(searchString && { $search: searchString }),
        ...(isSms && { $sms: true }),
        $filterEngine: getEffectiveFilterEngine(),
      },
    }
  }

  const filterParts = [
    `taskOid=${messageTaskOid}`,
    excludeRejected ? 'status != rejected' : undefined,
    generateStatusFilter(statuses, combineStatusOp),
    searchString ? generateSearchFilter(searchString, searchStringTargets) : undefined,
  ].filter(Boolean)
  return {
    endpointKey: isSms ? 'fetchSmsFanMessages' : 'fetchFanMessages',
    params: {
      ...baseParams,
      $orderby: makeSafeOrderbyParams(orderBy),
      $filter: filterParts.join(' AND '),
    },
  }
}

/** fan-message-excluded returns a slightly different structure than fan-message. */
export function mapSuppressedRecipients(data: MessageRecipient[]): MessageRecipient[] {
  return data.map(item => ({
    ...item,
    'fan': item.fan || {},
    'status': item.status || 'suppressed',
    'sysMtime': item.sysMtime || null,
    'promoterMessageOid': item.promoterMessageOid || null,
    'additional-information': item['additional-information'] || null,
  }))
}
